import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppShell, Container, Group, Text, Title } from "@mantine/core";
import { signOut } from "@/utils/auth";

interface PageShellProps {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Rendered at the right of the title row, e.g. filter controls. */
  actions?: ReactNode;
}

const NAV: { to: string; label: string }[] = [
  { to: "/", label: "Stores" },
  { to: "/hours", label: "Opening hours" },
  { to: "/deployments", label: "CV deployments" },
];

export function PageShell({
  title,
  subtitle,
  children,
  actions,
}: PageShellProps) {
  const location = useLocation();

  const isCurrent = (to: string) =>
    to === "/"
      ? location.pathname === "/" || location.pathname.startsWith("/store/")
      : location.pathname.startsWith(to);

  return (
    <AppShell padding="md" header={{ height: 60 }}>
      <AppShell.Header>
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="lg" wrap="nowrap" style={{ minWidth: 0 }}>
              <Text fw={700} size="lg" style={{ whiteSpace: "nowrap" }}>
                Store Management
              </Text>
              <Group gap="md" wrap="nowrap">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    style={{
                      color: isCurrent(item.to)
                        ? "var(--mantine-color-blue-6)"
                        : "var(--mantine-color-text)",
                      textDecoration: "none",
                      fontWeight: isCurrent(item.to) ? 600 : 400,
                      fontSize: 14,
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </Group>
            </Group>
            <Text
              size="xs"
              c="dimmed"
              style={{ cursor: "pointer" }}
              onClick={signOut}
            >
              Sign out
            </Text>
          </Group>
        </Container>
      </AppShell.Header>
      <AppShell.Main>
        <Container size="xl">
          <Group
            justify="space-between"
            align="flex-end"
            wrap="wrap"
            gap="sm"
            mb="lg"
          >
            <div>
              <Title order={2}>{title}</Title>
              {subtitle ? (
                <Text size="sm" c="dimmed" mt={4}>
                  {subtitle}
                </Text>
              ) : null}
            </div>
            {actions}
          </Group>
          {children}
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
